import { ws } from './state';
import { resyncEncounter } from './encounter-delta';

type Envelope = { channel: string; seq: number; epoch: string; frame: unknown };
type Control = { type?: string; channel?: string; seq?: number; epoch?: string; since?: number; replay?: boolean; status?: string };
type Delivery = { envelope: Envelope; apply: (frame: unknown) => void | Promise<void> };
type Cursor = { last?: number; epoch?: string; pending: Map<number, Delivery>; busy: boolean; requested: boolean;
    applying?: Promise<void>; healing?: Promise<void>; gap?: string; failed?: number };

// One cursor per encounter, retained across sockets. Only acknowledged application advances it.
export function createEncounterReplay(send: (control: Record<string, unknown>) => void,
    heal: (encounterId: string) => Promise<unknown>) {
    const cursors = new Map<string, Cursor>();
    const key = (channel: string): string => channel.replace(/^encounter:/i, 'encounter:').toUpperCase();
    function cursor(channel: string): Cursor {
        const name = key(channel);
        let state = cursors.get(name);
        if (!state) { state = { pending: new Map(), busy: false, requested: false }; cursors.set(name, state); }
        return state;
    }
    function request(channel: string, state: Cursor): void {
        if (state.requested || state.last === undefined) return;
        state.requested = true;
        send({ type: 'subscribe', channel, since: state.last, epoch: state.epoch });
    }
    async function drain(channel: string, state: Cursor): Promise<void> {
        if (state.busy || state.healing || state.last === undefined) return;
        state.busy = true;
        try {
            let delivery: Delivery | undefined;
            while (!state.healing && (delivery = state.pending.get(state.last + 1))) {
                if (delivery.envelope.epoch !== state.epoch) { request(channel, state); break; }
                state.applying = Promise.resolve(delivery.apply(delivery.envelope.frame));
                await state.applying;
                state.applying = undefined;
                state.pending.delete(delivery.envelope.seq);
                state.last = delivery.envelope.seq;
            }
            if (state.pending.size) request(channel, state);
        } catch (error) {
            // Leave the failed sequence pending and unacknowledged for one replay request.
            state.applying = undefined;
            if (!state.healing && state.failed !== state.last + 1) {
                state.failed = state.last + 1;
                request(channel, state);
            }
            console.warn('[websocket] ordered apply failed', error);
        } finally { state.busy = false; }
    }
    return {
        subscription(channel: string): { since?: number; epoch?: string } {
            if (!channel.toLowerCase().startsWith('encounter:')) return {};
            const state = cursor(channel);
            state.requested = false;
            return { since: state.last, epoch: state.epoch };
        },
        closed(): void { for (const state of cursors.values()) { state.requested = false; state.failed = undefined; } },
        receive(value: unknown, apply: Delivery['apply']): boolean {
            const envelope = value as Envelope;
            if (!envelope || typeof envelope.channel !== 'string' || !envelope.channel.startsWith('encounter:')
                || !Number.isSafeInteger(envelope.seq) || envelope.seq < 1 || typeof envelope.epoch !== 'string' || !('frame' in envelope)) return false;
            const state = cursor(envelope.channel);
            if (state.last !== undefined && envelope.seq <= state.last && envelope.epoch === state.epoch) return true;
            state.pending.set(envelope.seq, { envelope, apply });
            // The server closes overflowing live sockets; keep the browser's held suffix bounded too.
            if (state.pending.size > 2000) state.pending.delete(state.pending.keys().next().value!);
            void drain(envelope.channel, state);
            return true;
        },
        control(control: Control): boolean {
            const channel = control.channel;
            if (!channel?.startsWith('encounter:')) return false;
            const state = cursor(channel);
            if (control.type === 'transport_subscription' && control.replay && control.status === 'subscribed') {
                if (state.last === undefined) { state.last = control.seq ?? 0; state.epoch = control.epoch; }
                return false; // Setup still emits the subscription acknowledgment.
            }
            if (control.type === 'transport_replay_gap' && Number.isSafeInteger(control.seq)) {
                const identity = `${control.epoch}:${control.seq}`;
                if (state.gap === identity) return true;
                state.gap = identity;
                const head = control.seq!;
                state.healing = Promise.resolve(state.applying).catch(() => {}).then(() => heal(channel.slice('encounter:'.length))).then(() => {
                    state.last = head; state.epoch = control.epoch; state.failed = undefined;
                    for (const seq of state.pending.keys()) if (seq <= head) state.pending.delete(seq);
                }).catch(error => console.warn('[websocket] replay gap heal failed', error))
                    .finally(() => { state.healing = undefined; void drain(channel, state); });
                return true;
            }
            if (control.type === 'transport_replay_complete') {
                // Replay has exhausted this epoch; stale foreign frames cannot be filled by another request.
                for (const [seq, delivery] of state.pending) {
                    if (delivery.envelope.epoch !== control.epoch
                        || (state.epoch === control.epoch && state.last !== undefined && seq <= state.last)) state.pending.delete(seq);
                }
                state.requested = false;
                void drain(channel, state);
                return true;
            }
            return false;
        },
    };
}

export const encounterReplay = createEncounterReplay(control => {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(control));
}, resyncEncounter);
